// DocumentClassificationGuide
//
// One source of truth for explaining the five document-classification
// tiers. Surfaced in three places:
//
//   * Documents page — collapsible explainer next to the picker, plus
//     a FirstVisitTips banner the first time someone opens the page.
//   * Help center — full-article variant.
//   * Onboarding setup checklist — linked from the "Understand
//     document security" step.
//
// Two render variants:
//
//   * variant="compact"  — accordion that collapses to a one-line
//     "What do these mean?" link, expands to a tier-by-tier table.
//     Designed for inline use where vertical space is precious
//     (upload modal, document detail panel).
//
//   * variant="full"     — always-expanded, includes the "practical
//     guidance" section (recommended tiers per document type) and
//     the honest "what's not enforced" footnote. Designed for the
//     help center and onboarding screens.
//
// Both variants share the same `TIERS` data so a wording change
// updates every surface. The data here MIRRORS the RLS enforcement
// in migration 068 — if you change a row here, also check that the
// description matches what the policy actually does.

import React, { useState } from 'react';
import {
  Globe, Users, Lock, Scale, ShieldAlert,
  ChevronDown, ChevronRight, FileText, Info,
} from 'lucide-react';

// ─── Tier definitions ─────────────────────────────────────────────
// Mirrors migration 068's RLS policies. Each entry pairs a plain-
// language description with the actual server-side enforcement so
// the user can trust what they read.
export const CLASSIFICATION_TIERS = [
  {
    value:      'public',
    label:      'Public',
    Icon:       Globe,
    tone:       'emerald',
    whoSees:    'Every firm staff member + the client via the portal (when portal_visible is on).',
    enforcement:'RLS allows authenticated firm users; portal clients see it on their portal.',
    suggested:  'Court-filed pleadings, blank retainer agreements, intake forms, public marketing.',
  },
  {
    value:      'internal',
    label:      'Internal',
    Icon:       Users,
    tone:       'blue',
    whoSees:    'Every firm staff member only — never appears in any client portal.',
    enforcement:'RLS blocks portal clients regardless of portal_visible flag.',
    suggested:  'Internal templates, staff memos, time-sheet exports, firm policies.',
  },
  {
    value:      'confidential',
    label:      'Confidential',
    Icon:       Lock,
    tone:       'amber',
    whoSees:    'Every firm staff member — but every view and download is recorded in the access log.',
    enforcement:'Same RLS as internal; application logs every read to document_access_log with user, timestamp, IP.',
    suggested:  'Signed client contracts, financial records, employee records.',
  },
  {
    value:      'privileged',
    label:      'Privileged',
    Icon:       Scale,
    tone:       'orange',
    whoSees:    'Only users with role Admin, Partner, Attorney, or Associate. Paralegals, IT, clerks, and support staff cannot see it.',
    enforcement:'RLS calls is_attorney_or_above() — checks both the legacy role column AND the RBAC role assignment.',
    suggested:  'Counsel-only strategy memos, attorney work product, mental impressions, settlement negotiation notes.',
  },
  {
    value:      'strictly_confidential',
    label:      'Strictly Confidential',
    Icon:       ShieldAlert,
    tone:       'red',
    whoSees:    'Only Admins, or users explicitly named in the document\'s allow-list. A Partner cannot open it unless their name is on the list for that specific document.',
    enforcement:'RLS checks auth.uid() = ANY(allowed_user_ids) per document. Manage the list from the document detail page.',
    suggested:  'Whistleblower documents, sensitive HR investigations, witness identities in criminal matters.',
  },
];

// Tailwind class lookups by tone so the colour story stays consistent
// across variants and matches the rest of the Documents page.
const TONE = {
  emerald: { dot: 'bg-emerald-500', text: 'text-emerald-700 dark:text-emerald-300', bg: 'bg-emerald-50 dark:bg-emerald-900/20' },
  blue:    { dot: 'bg-blue-500',    text: 'text-blue-700 dark:text-blue-300',       bg: 'bg-blue-50 dark:bg-blue-900/20' },
  amber:   { dot: 'bg-amber-500',   text: 'text-amber-700 dark:text-amber-300',     bg: 'bg-amber-50 dark:bg-amber-900/20' },
  orange:  { dot: 'bg-orange-500',  text: 'text-orange-700 dark:text-orange-300',   bg: 'bg-orange-50 dark:bg-orange-900/20' },
  red:     { dot: 'bg-red-500',     text: 'text-red-700 dark:text-red-300',         bg: 'bg-red-50 dark:bg-red-900/20' },
};

// ─── Compact (inline / accordion) variant ─────────────────────────
function CompactGuide() {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50/40 dark:bg-slate-800/40">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-3 py-2 text-left text-xs font-medium text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white transition"
      >
        {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
        <Info size={13} className="text-indigo-500" />
        What do these classifications mean?
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-2">
          {CLASSIFICATION_TIERS.map(({ value, label, Icon, tone, whoSees }) => {
            const t = TONE[tone];
            return (
              <div key={value} className={`flex gap-2 p-2 rounded ${t.bg}`}>
                <Icon size={14} className={`${t.text} flex-shrink-0 mt-0.5`} />
                <div className="min-w-0">
                  <p className={`text-xs font-semibold ${t.text}`}>{label}</p>
                  <p className="text-[11px] text-slate-600 dark:text-slate-300 leading-snug">{whoSees}</p>
                </div>
              </div>
            );
          })}
          <p className="text-[10px] text-slate-500 dark:text-slate-400 pt-1">
            These rules are enforced by the database, not just the UI — a user who lacks permission can&apos;t see the file even by guessing the URL.
          </p>
        </div>
      )}
    </div>
  );
}

// ─── Full (help-article / onboarding) variant ─────────────────────
function FullGuide() {
  return (
    <div className="space-y-5 text-sm text-slate-700 dark:text-slate-200">
      <div className="flex gap-3 p-4 rounded-xl bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800">
        <FileText size={18} className="text-indigo-600 dark:text-indigo-300 flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-semibold text-indigo-900 dark:text-indigo-100">Five tiers — actually enforced by the database</p>
          <p className="text-xs text-indigo-800/80 dark:text-indigo-200/80 mt-1">
            These aren&apos;t decorative labels. Each tier maps to a Row-Level-Security
            policy that controls who can read, download, and edit the file. A user who
            doesn&apos;t qualify never sees the document — not even an &quot;access denied&quot;
            error, just no row in the list.
          </p>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 dark:border-slate-700">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-500 dark:text-slate-400 uppercase text-[10px] tracking-wider">
            <tr>
              <th className="text-left px-3 py-2 font-semibold">Tier</th>
              <th className="text-left px-3 py-2 font-semibold">Who can see it</th>
              <th className="text-left px-3 py-2 font-semibold">Use it for</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {CLASSIFICATION_TIERS.map(({ value, label, Icon, tone, whoSees, suggested }) => {
              const t = TONE[tone];
              return (
                <tr key={value} className="align-top">
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex w-6 h-6 rounded-md items-center justify-center ${t.bg}`}>
                        <Icon size={13} className={t.text} />
                      </span>
                      <span className={`font-semibold ${t.text}`}>{label}</span>
                    </div>
                  </td>
                  <td className="px-3 py-3 text-slate-600 dark:text-slate-300 leading-snug">
                    {whoSees}
                  </td>
                  <td className="px-3 py-3 text-slate-500 dark:text-slate-400 leading-snug">
                    {suggested}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="space-y-2">
        <p className="font-semibold text-slate-900 dark:text-white">How the protection actually works</p>
        <ul className="list-disc pl-5 space-y-1.5 text-xs text-slate-600 dark:text-slate-300">
          <li>
            <strong>Database layer.</strong> Every read of the documents table runs through an
            RLS policy. If your role doesn&apos;t qualify, the row just doesn&apos;t come
            back — no &quot;permission denied&quot; error, the file is invisible.
          </li>
          <li>
            <strong>Storage layer.</strong> The actual file in Supabase Storage is gated by the
            same firm scope. Even if a user obtained the storage path, they can&apos;t fetch it.
          </li>
          <li>
            <strong>Audit log.</strong> Every view, download, edit, and classification change is
            recorded with user id, timestamp, action, and tier. Useful when a court asks
            &quot;who accessed file X on date Y?&quot;.
          </li>
          <li>
            <strong>Strictly Confidential adds a per-document allow-list</strong> that an Admin
            manages from the document detail page.
          </li>
        </ul>
      </div>

      <div className="rounded-lg border border-amber-200 dark:border-amber-700/50 bg-amber-50/60 dark:bg-amber-900/20 p-3 space-y-1.5">
        <p className="text-xs font-semibold text-amber-800 dark:text-amber-200">What is NOT enforced</p>
        <ul className="list-disc pl-4 space-y-1 text-[11px] text-amber-800/90 dark:text-amber-200/90">
          <li>
            No watermarking or no-print restrictions. A Partner who downloads a Privileged file
            can email or print it. Classification controls who can fetch the file, not what
            they do with it afterwards.
          </li>
          <li>
            Files uploaded before classification was introduced default to <em>Internal</em>.
            Older sensitive documents need a manual tier bump.
          </li>
        </ul>
      </div>
    </div>
  );
}

export default function DocumentClassificationGuide({ variant = 'compact' }) {
  return variant === 'full' ? <FullGuide /> : <CompactGuide />;
}
